/**
 * THE EIGHT ROWS — "What comes back?", and the step shape each one writes.
 *
 * PURE. No Vue, no store, no I/O. The picker in §5 is the one control that collapses three
 * of the seven step types into one idea, and the collapse has to be reversible: a stored
 * step must read back onto exactly the row that would have written it, or opening a task
 * moves the dot to a row the owner never chose.
 *
 * ── WHY THREE TYPES ARE ONE QUESTION ──────────────────────────────────────────────────
 * `ask`, `choose` and `choose_live` were never three ideas. They are three answers to one
 * question — *where does the answer come from?* — and their names are the two hardest in
 * the product to tell apart (`TaskStepTypePicker.vue`'s own docblock says so). An `ask`
 * additionally carries a `kind`, which is a second question wearing the first one's clothes:
 * "a date" and "a time" are not step types, they are what the step will accept.
 *
 * So one list of eight, and the shape falls out of the row:
 *
 *   1-6  a SHAPE   → { type: 'ask', kind }          what the answer has to look like
 *   7    a SOURCE  → { type: 'choose' }             the owner types the choices
 *   8    a SOURCE  → { type: 'choose_live' }        her own system sends them
 *
 * ── THE SEAM AT ROW 7, STATED RATHER THAN HIDDEN ──────────────────────────────────────
 * Rows 1-6 and rows 7-8 are NOT the same kind of thing, and an owner reading top to bottom
 * meets that change with nothing to mark it. `group` is that mark: the picker prints one
 * rule between the two, and if the eight ever read as eight peers the grouping is already
 * here to split them back into two controls without touching a caller.
 *
 * ── AND THE ONE RULE A PICKER CANNOT BE TRUSTED WITH ──────────────────────────────────
 * Which rows hold a bilingual question. Until P-10.24 row 8 did not: the sidecar's schema
 * refused `questionEn`/`questionAr` on `choose_live` under `.strict()`. The chat now sends an
 * owner's question as written, so a live list holds one like every other row. It is still
 * never a card field, because its rows are fetched while the customer waits;
 * `task-arabic.js` `canCarryCardWords` says that on its own. `carriesQuestion` below is the
 * rule as data, so no screen has to remember it.
 */
import { ANSWER_KINDS } from './task-flow-draft.js';

/** Which half of the list a row belongs to — what the answer looks like, or where it comes from. */
export const ANSWER_GROUPS = Object.freeze({
  SHAPE: 'shape',
  SOURCE: 'source',
});

const row = (id, group, shape, carriesQuestion) =>
  Object.freeze({
    id,
    group,
    /** The step `type` this row writes. */
    type: shape.type,
    /** The `kind` this row writes, on the six rows that have one. */
    kind: shape.kind,
    /** Whether a step written from this row can carry `questionEn`/`questionAr`. */
    carriesQuestion,
    labelKey: `WASLO.TASKS.COMPOSE.ANSWER.${id.toUpperCase()}.LABEL`,
    hintKey: `WASLO.TASKS.COMPOSE.ANSWER.${id.toUpperCase()}.HINT`,
  });

/**
 * In the order they are read, which is also the order they are drawn.
 *
 * The first six ids are deliberately the sidecar's own `AnswerKind` spellings rather than
 * new names: a row id that differs from the kind it writes is one more table to keep in
 * step, for no reader's benefit.
 */
export const ANSWER_ROWS = Object.freeze([
  row('text', ANSWER_GROUPS.SHAPE, { type: 'ask', kind: 'text' }, true),
  row('number', ANSWER_GROUPS.SHAPE, { type: 'ask', kind: 'number' }, true),
  row('date', ANSWER_GROUPS.SHAPE, { type: 'ask', kind: 'date' }, true),
  row('time', ANSWER_GROUPS.SHAPE, { type: 'ask', kind: 'time' }, true),
  row('email', ANSWER_GROUPS.SHAPE, { type: 'ask', kind: 'email' }, true),
  row('phone', ANSWER_GROUPS.SHAPE, { type: 'ask', kind: 'phone' }, true),
  // Services field fixes G2.4: what the customer sends rather than types. Still "what the answer
  // looks like", so above the rule.
  row('photo', ANSWER_GROUPS.SHAPE, { type: 'ask', kind: 'photo' }, true),
  row('file', ANSWER_GROUPS.SHAPE, { type: 'ask', kind: 'file' }, true),
  row('written', ANSWER_GROUPS.SOURCE, { type: 'choose' }, true),
  // A live list carries a question too since P-10.24: the chat sends it as written. It is still
  // never a card field (`task-arabic.js` `canCarryCardWords` says so on its own).
  row('live', ANSWER_GROUPS.SOURCE, { type: 'choose_live' }, true),
]);

export const ANSWER_ROW_IDS = Object.freeze(ANSWER_ROWS.map(r => r.id));

/** The sentence printed between the two halves, so the seam at row 7 is said out loud. */
export const ANSWER_GROUP_RULE_KEY = 'WASLO.TASKS.COMPOSE.ANSWER.SOURCE_RULE';

const BY_ID = new Map(ANSWER_ROWS.map(r => [r.id, r]));

/** One row, or `null` for an id this list has never had. Total; never throws. */
export const answerRow = id => BY_ID.get(id) ?? null;

export const answerRowsIn = group => ANSWER_ROWS.filter(r => r.group === group);

/**
 * The step fields a row writes — a FRESH object each time, so a caller that spreads it into
 * a draft and then edits the draft cannot reach back into the frozen table.
 *
 * `kind` is present only on the six rows that have one, rather than `undefined`: the wire
 * shape is `.strict()`, and a `choose` carrying `kind: undefined` survives `JSON.stringify`
 * as a dropped key here and as a refused one the day anything sends it unstringified.
 */
export const shapeForRow = id => {
  const found = answerRow(id);
  if (!found) return null;
  return found.kind === undefined
    ? { type: found.type }
    : { type: found.type, kind: found.kind };
};

/**
 * Which row wrote this step — the read-back half, and the one that has to be exact.
 *
 * `null` rather than a guess in all three of the ways it can fail, because every guess here
 * puts a dot on a radio the owner did not press:
 *   • a step that asks nothing at all (an action, a message, a hand-over, an ending);
 *   • an `ask` whose `kind` is not one of the six — a graph from a newer sidecar, which
 *     this builder must not silently re-label as "anything they type" and save back;
 *   • anything that is not a step object.
 */
export const rowForStep = step => {
  const type = step && typeof step === 'object' ? step.type : undefined;
  if (type === 'choose') return 'written';
  if (type === 'choose_live') return 'live';
  if (type !== 'ask') return null;
  return ANSWER_KINDS.includes(step.kind) ? step.kind : null;
};

/**
 * Whether a step of this shape can hold the customer-facing question in both languages.
 *
 * Read from the table rather than re-tested, so the rule is stated once.
 */
export const carriesQuestion = type =>
  ANSWER_ROWS.some(r => r.type === type && r.carriesQuestion);

export default ANSWER_ROWS;
